import { useState, useEffect, useMemo, React } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/DeviceRepair.css';
import FormContainer from '../CustomElements/FormContainer';
import CustomButton from '../CustomElements/CustomButton';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import CustomTextarea from '../CustomElements/CustomTextarea';
import CustomTitle from '../CustomElements/CustomTitle';
import CustomContainer from '../CustomElements/CustomContainer';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import PhoneInput from '../CustomElements/PhoneInput';
import FilePicker from '../FileManager/FilePicker';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useFormDataChanged from '../../customHook/useFormDataChanged';
import useSuccessListener from '../../customHook/useSuccessListener';
// Утилиты
import { handleUniversalChange } from '../../utils/formUtils';
import { convertToDate, transformFieldsToMoscowStart } from '../../utils/convertToDate';
import { omitFields } from '../../utils/omitFields';
import { removeEmptyStrings } from '../../utils/removeEmptyStrings';
import { prepareObjectForSubmit } from '../../utils/prepareObjectForSubmit';
// Действия Redux
import { transferDeviceForRepair } from '../../redux/actions/devices.action';
import { editRepairStatusFromServer } from '../../redux/actions/statuses.action';
import { load } from '../../redux/actions/loader.action';

// компонент изменения статуса "Направление в ремонт" и для передачи устройства в ремонт(фигма 2.4.3)

function TransferDeviceToRepair({ currentDevice, closeModal, currentStatus, title, isEdit, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();

  // получаем опции из стора
  const optionsForSelect = useSelector((state) => state.options);

  // Состояния для кнопки и уведомления об успешном добавлении
  const [addSuccess, setAddSuccess] = useState(false);

  const optionalFieldsObj = {
    broken: ['serviceCenterContact'],
    notBroken: ['serviceCenterContact', 'defectsFile', 'defectsFileName'],
  };

  const [optionalFields, setOptionalFields] = useState(optionalFieldsObj.notBroken);

  // отмена подтверждения
  const closeStatusEditModal = () => {
    setAddSuccess(false);
  };

  // Состояние для формы. Мемоизируем начальные данные формы
  const initialFormData = useMemo(
    () =>
      currentStatus
        ? {
            id: currentStatus.id,
            deviceId: currentDevice.id,
            date: convertToDate(currentStatus.date),
            acceptanceCert: currentStatus.acceptanceCert,
            acceptanceCertName: currentStatus?.acceptanceCertName,
            serviceCenterId: currentStatus.serviceCenterId || '',
            serviceCenterContact: currentStatus.serviceCenterContact || '',
            senderId: currentStatus.senderId || '',
            defectsFile: currentStatus.defectsFile || '',
            defectsFileName: currentStatus?.defectsFileName || '',
            isBroken: currentStatus?.broken,
            note: currentStatus.note || '',
          }
        : {
            deviceId: currentDevice.id,
            date: '',
            acceptanceCert: '',
            serviceCenterId: '',
            serviceCenterContact: '',
            senderId: '',
            defectsFile: '',
            isBroken: '',
            note: '',
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStatus]
  );
  const [formData, setFormData] = useState(initialFormData);

  // Обновляем formData при изменении currentStatus
  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  //проверяем были ли изменения в форме (true-да, true-нет)
  const isFormEdit = useFormDataChanged(initialFormData, formData, isEdit);

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, optionalFields);

  // В зависимости от состояния устройства, меняем обязательные поля
  useEffect(() => {
    if (formData?.isBroken) {
      setOptionalFields(optionalFieldsObj.broken);
    }
    if (!formData?.isBroken) {
      setOptionalFields(optionalFieldsObj.notBroken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.isBroken]);

  // Обработчик отправки формы для передачи устройства в ремонт
  const handlerSubmit = (event) => {
    event.preventDefault();
    // Если редактирование — открываем модалку подтверждения и выходим
    if (isEdit) {
      setAddSuccess(true);
      return;
    }
    // Устанавливаем состояние загрузки
    dispatch(load());
    // Удаляем поля, которые не нужно отправлять
    const formWithoutExcluded = omitFields(formData, ['deviceId', 'acceptanceCertName']);
    // Удаляем пустые строки
    const formWithoutEmptyStrings = removeEmptyStrings(formWithoutExcluded);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutEmptyStrings, ['date']);
    // Отправляем данные на сервер
    dispatch(
      transferDeviceForRepair({
        payload,
        deviceId: formData?.deviceId,
        typeId: currentDevice?.typeId,
        path: 'repair',
      })
    );
  };

  // Обработчик отправки формы для редактирования статуса
  const handlerSubmitEdit = (event) => {
    event.preventDefault();
    //переводим в состояние загрузки
    dispatch(load());
    // сравниваем начальное и текущее состояние заменяем на null пустые строки для удаленных полей
    const formDataWithEmptyStrings = prepareObjectForSubmit(initialFormData, formData);
    // Удаляем поля, которые не нужно отправлять
    const formWithoutExcludedFields = omitFields(formDataWithEmptyStrings, [
      'deviceId',
      'acceptanceCertName',
      'defectsFileName',
    ]);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutExcludedFields, ['date']);
    // Отправляем данные на сервер через Redux
    dispatch(
      editRepairStatusFromServer({
        payload,
        deviceId: currentDevice?.id,
        typeId: currentDevice?.typeId,
        statusId: formData?.id,
        path: 'repair',
      })
    );
  };

  //отслеживаем выполнение запроса
  useSuccessListener(setFormData, initialFormData, closeModal, closeStatusEditModal);

  return (
    <CustomContainer>
      <CustomTitle
        titleText={title || t?.sendForRepairTitle || 'НАПРАВЛЕНИЕ В РЕМОНТ УСТРОЙСТВА'}
        deviceType={currentDevice.type}
        deviceId={currentDevice.id}
      />
      {addSuccess && (
        <ConfirmationPage
          confirmText={t?.statusChangeConfirmationText || 'Вы уверены, что хотите изменить данные статуса?'}
          confirmButtonText={t?.confirmButton || 'Изменить'}
          cancelButtonText={t?.cancelButton || 'Отменить'}
          onConfirm={handlerSubmitEdit} // Функция, которая должна быть вызвана при подтверждении
          onCancel={closeStatusEditModal} // Функция для закрытия модального окна или отмены действия
        />
      )}
      {!addSuccess && (
        <FormContainer>
          <DateSelector
            column="left"
            width="100%"
            height="var(--sadr-font-size-60)"
            label={t?.repairSendDateLabel || 'Дата отправки в ремонт *'}
            selectedDate={formData.date}
            setSelectedDate={(date) => handleUniversalChange(formData, setFormData, null, 'date', date, 'date')}
            name="date"
            placeholder={t?.placeholderSelectDate || 'Выберите дату'}
            minDate={convertToDate(currentDevice?.lastActionDate)}
            maxDate={new Date()}
          />
          <Selector
            column="left"
            options={optionsForSelect.serviceCenters}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'serviceCenterId', selectedOption, 'select')
            }
            label={t?.serviceCenterLabel || 'Сервисный центр *'}
            placeholder={t?.serviceCenterPlaceholder || 'Выберите сервисный центр'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={optionsForSelect.serviceCenters?.find((option) => option.value === formData.serviceCenterId)}
            isMulti={false}
            name="serviceCenterId"
          />
          <Selector
            column="left"
            options={optionsForSelect.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'senderId', selectedOption, 'select')
            }
            placeholder={t?.placeholderSelectEmployee || 'Выберите ФИО'}
            label={t?.employeeLabel || 'ФИО направившего в ремонт *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={optionsForSelect.employees?.find((option) => option.value === formData.senderId)}
            isMulti={false}
            name="senderId"
          />
          <FilePicker
            column="left"
            label={t?.labelAcceptanceCert || 'Акт приема-передачи *'}
            formData={formData}
            setFormData={setFormData}
            fieldName="acceptanceCertName"
            fieldIdName="acceptanceCert"
            rowSpan={2}
          />

          {/* Заглушка, чтобы сместить часть формы */}
          <div className="empty-placeholder" column="right" />

          <PhoneInput
            column="right"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            label={t?.serviceCenterContactsLabel || 'Контактные данные сервисного центра'}
            name="serviceCenterContact"
            value={formData.serviceCenterContact}
          />
          <Selector
            column="right"
            options={optionsForSelect.condition.filter((item) => item.value !== undefined)}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'isBroken', selectedOption, 'select')
            }
            label={t?.labelCondition || 'Состояние *'}
            placeholder={t?.placeholderCondition || 'Не определено'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={
              formData.isBroken === undefined
                ? optionsForSelect.condition?.find(
                    (option) => option.label === (t?.placeholderCondition || 'Не определено')
                  )
                : formData.isBroken === ''
                ? null
                : optionsForSelect.condition?.find((option) => option.value === formData.isBroken)
            }
            isMulti={false}
            name="isBroken"
          />
          <FilePicker
            column="right"
            label={`${t?.defectsReportTitle || 'Дефектная ведомость'}${formData?.isBroken ? ' *' : ''}`}
            formData={formData}
            setFormData={setFormData}
            fieldName="defectsFileName"
            fieldIdName="defectsFile"
            rowSpan={2}
          />
          <CustomTextarea
            type="textArea"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            name="note"
            label={t?.labelComment || 'Комментарий *'}
            placeholder={t?.placeholderComment || 'Оставьте комментарий'}
            columnspan={2}
            width="100%"
            height="100%"
            value={formData.note}
          />
          <CustomButton
            onClick={(event) => {
              handlerSubmit(event);
            }}
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            color={isButtonDisabled ? '#ADADAD' : ''}
            text={
              isEdit ? t?.buttonSaveChanges || 'Сохранить изменения' : t?.statusSendForRepair || 'Направить в ремонт'
            }
            disabled={isButtonDisabled || !isFormEdit}
            isButton
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
}

export default TransferDeviceToRepair;
